import { httpClient } from '@bs/core'

export interface CategoryItem {
  id: string
  name: string
  slug: string
  parentId?: string
  productCount?: number
  children?: CategoryItem[]
}

interface ApiCategoryItem {
  id: number | string
  name: string
  slug: string
  parent?: number | string | null
  count?: number
}

// Verbatim mirror of legacy
// frontend/src/pages/Category/PantryCategory/hooks.ts:14-38 RawWebCategoryItem.
// The /stores/:storeId/categories endpoint returns these rows; the feature
// layer (CategoryContext) feeds them through buildWebCategoryTree to produce
// the WebCategory tree the UI consumes.
export interface RawWebCategoryItem {
  cova_category?: {
    id: string | number
    is_mapped: boolean
    name: string
  }[]
  greenline_category?: {
    id: string | number
    is_mapped: boolean
    name: string
  }[]
  pos_mapping?: { category_ids?: string[] }
  id: string | number
  name: string
  slug: string
  // Path from the root joined by "/" (e.g. "concentrates/live-resin"); unique
  // per store and used for every storefront URL. `slug` is only the leaf and
  // is unique within its parent. Rows not yet backfilled omit it — fall back
  // to `slug`.
  full_slug?: string
  parent?: number | string | null
  count?: number
  visible?: boolean
  headline?: string
  description?: string
  seo?: {
    title?: string
    meta_description?: string
  }
  thumbnail?: unknown
  image?: unknown
  banner_image?: unknown
  bannerImage?: unknown
  // Number of active 301 redirects that point at this category — sourced from
  // `category_slug_history` via a $lookup on the backend list endpoint
  // (paginate_product_categories_v2). Excludes the operational row whose
  // `slug` equals the current slug. Older API responses may omit this field;
  // treat absent as `0`.
  redirect_count?: number
  // Whether the slug is "locked" to a manually-set value. When false (or
  // absent on legacy rows), the drawer auto-derives the slug from the display
  // name as the user types. Purely a UX hint — backend stores it but never
  // enforces it (e.g. PUT still accepts a slug change while locked). Defaults
  // to `false` on every legacy category record that pre-dates this field.
  slug_locked?: boolean
}

// A single row from `category_slug_history` surfaced on GET category detail.
// `slug` is the full path (may contain "/"). `id` is the Mongo `_id` stringified — the canonical handle used by the
// DELETE endpoint. `created_at` mirrors the backend `_created` field and is
// passed through as the raw ISO string the API emits (formatted at render).
export interface SlugHistoryEntry {
  id: string
  slug: string
  created_at?: string
}

// Wire shape returned by GET /stores/:storeId/categories/:categoryId. Backend
// returns the full record (with Eve-style `_id`/`_created`/`_updated`/`_links`
// envelope fields and a `store_id` echo) wrapped in
// `{ data, message, status }`. `httpClient.json.get` unwraps the outer
// envelope via `defaultResponseHandler`, so the gateway receives the inner
// `data` object directly. Field set is a superset of RawWebCategoryItem;
// optional fields documented below mirror sample responses observed in
// production (verified 2026-06-01).
export interface CategoryDetailResponse extends RawWebCategoryItem {
  _id?: string
  _created?: string
  _updated?: string
  _links?: unknown
  store_id?: string
  display?: string
  menu_order?: number
  greenline_mapping?: { category_ids?: Array<string | number> }
  // Active 301 redirects (previous slugs that still resolve to this category).
  // Backend filters out the row that mirrors the current slug, so this list
  // is exactly the chips the drawer should render.
  slug_history?: SlugHistoryEntry[]
}

function mapFromApi(item: ApiCategoryItem): CategoryItem {
  return {
    id: String(item.id),
    name: item.name,
    slug: item.slug,
    parentId: item.parent ? String(item.parent) : undefined,
    productCount: item.count ?? 0,
  }
}

function buildTree(flat: CategoryItem[]): CategoryItem[] {
  const map = new Map<string, CategoryItem>()
  const roots: CategoryItem[] = []

  flat.forEach((cat) => map.set(cat.id, { ...cat, children: [] }))

  map.forEach((cat) => {
    if (cat.parentId && map.has(cat.parentId)) {
      map.get(cat.parentId)!.children!.push(cat)
    } else {
      roots.push(cat)
    }
  })

  return roots
}

// TODO: Verify backend endpoint. Based on existing app pattern the URL is /cova/stores/:storeId/categories
// but category endpoints may be shared across POS types via a unified route.
// Confirm with backend team whether /stores/:storeId/categories exists or if it is POS-prefixed.
const categoryUrl = (storeId: string) => `/stores/${storeId}/categories`

export interface CategoryImageInput {
  id?: string | number
  name?: string
  src?: string
  alt?: string
}

export interface CreateCategoryInput {
  name: string
  slug?: string
  // When true, the drawer treats the slug as user-pinned: typing in Display
  // Name will NOT auto-derive the slug. Sent through to the backend as
  // `item_data.slug_locked` so the lock state survives reload. Defaults to
  // `false` end-to-end when omitted.
  slugLocked?: boolean
  parentId?: string | number | null
  headline?: string
  description?: string
  visible?: boolean
  image?: CategoryImageInput | string | null
  bannerImage?: CategoryImageInput | string | null
  seoTitle?: string
  seoDescription?: string
}

interface CreateCategoryResponse {
  id?: number | string
  name?: string
  slug?: string
  parent?: number | string | null
  item?: ApiCategoryItem
  data?: ApiCategoryItem
}

// Mirror of legacy frontend/src/pages/Category/PantryCategory/validation.ts:28-33 toSlug().
// Backend requires `slug` on create; legacy UI auto-derives it from the display name when
// the user does not supply one. Duplicated here because pantry-features cannot import from
// the main app's ~/utils tree.
function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

// Mirror of legacy context.tsx:186-195 normalizeParentId().
// Backend expects `parent` to be `0` for top-level categories, a number for numeric IDs, or a
// string for slug-style IDs. Never `null`/`undefined`/empty string.
function normalizeParentId(
  parentId: string | number | null | undefined,
): string | number {
  if (parentId == null || parentId === '') return 0
  const asString = String(parentId)
  if (/^\d+$/.test(asString)) return Number(asString)
  return asString
}

// Mirror of legacy context.tsx:201-257 toCategoryImage(). Backend always wants the field
// present as { name, src, alt } (optionally with id) — empty strings if there is no image.
function toCategoryImage(
  value: CategoryImageInput | string | null | undefined,
): Record<string, unknown> {
  if (typeof value === 'string') {
    return { name: '', src: value, alt: '' }
  }
  if (!value || typeof value !== 'object') {
    return { name: '', src: '', alt: '' }
  }
  const result: Record<string, unknown> = {
    name: typeof value.name === 'string' ? value.name : '',
    src: typeof value.src === 'string' ? value.src : '',
    alt: typeof value.alt === 'string' ? value.alt : '',
  }
  if (value.id != null) result.id = value.id
  return result
}

function extractCreated(
  raw: CreateCategoryResponse | ApiCategoryItem,
): ApiCategoryItem | null {
  if (!raw) return null
  if ((raw as CreateCategoryResponse).item)
    return (raw as CreateCategoryResponse).item as ApiCategoryItem
  if ((raw as CreateCategoryResponse).data)
    return (raw as CreateCategoryResponse).data as ApiCategoryItem
  if (
    (raw as ApiCategoryItem).id !== undefined &&
    (raw as ApiCategoryItem).name !== undefined
  ) {
    return raw as ApiCategoryItem
  }
  return null
}

// Build the `item_data` payload shape required by both POST (create) and PUT
// (update). Mirrors legacy context.tsx:197-281 toItemData(). Backend validates
// the FULL schema even on partial updates, so we always emit every field.
function buildItemData(
  input: CreateCategoryInput,
  categoryId?: string,
): Record<string, unknown> {
  const name = input.name.trim()
  const slug = input.slug?.trim() ? toSlug(input.slug) : toSlug(name)

  const itemData: Record<string, unknown> = {
    name,
    slug,
    // Always emit a defined boolean so the backend never has to guess.
    // CreateCategorySerializer / UpdateCategorySerializer both accept this
    // field; it lives next to `slug` and is stored as-is on the category
    // document. See backend/be_api/.../serializers/categories.py.
    slug_locked: input.slugLocked ?? false,
    headline: input.headline?.trim() ?? '',
    description: input.description?.trim() ?? '',
    visible: input.visible ?? true,
    image: toCategoryImage(input.image),
    banner_image: toCategoryImage(input.bannerImage),
    seo: {
      title: input.seoTitle?.trim() ?? '',
      meta_description: input.seoDescription?.trim() ?? '',
    },
  }
  if (categoryId) itemData.id = categoryId
  if (input.parentId !== undefined) {
    itemData.parent = normalizeParentId(input.parentId)
  }
  return itemData
}

export const categoryApis = {
  getList: async (storeId: string): Promise<CategoryItem[]> => {
    const raw = (await httpClient.json.get(categoryUrl(storeId))) as
      | ApiCategoryItem[]
      | { items: ApiCategoryItem[] }
    const items = Array.isArray(raw) ? raw : (raw.items ?? [])
    return items.map(mapFromApi)
  },

  getTree: async (storeId: string): Promise<CategoryItem[]> => {
    const flat = await categoryApis.getList(storeId)
    return buildTree(flat)
  },

  // GET /stores/:storeId/categories/:categoryId — fetches the full record
  // for a single category, used by the drawer to source fresh field values
  // (headline / description / image / banner / SEO / visible flags) instead
  // of relying on the list cache that may lag behind PUTs. Mirrors legacy
  // frontend/src/pages/Category/hooks/useCategoryDetail.ts:21-23 getCategory.
  // `httpClient.json.get` unwraps the `{ data, message, status }` envelope
  // (see frontend/libs/bs-core/src/httpClient/interceptors.ts
  // `defaultResponseHandler`), so the resolved value here is the inner
  // category record — `null` if the backend returns no data field.
  getDetail: async (
    storeId: string,
    categoryId: string,
  ): Promise<CategoryDetailResponse | null> => {
    const raw = (await httpClient.json.get(
      `${categoryUrl(storeId)}/${categoryId}`,
    )) as CategoryDetailResponse | null
    return raw ?? null
  },

  // Mirror of legacy frontend/src/services/apis/category.ts:89-91 +
  // frontend/src/pages/Category/PantryCategory/hooks.ts:237-247.
  // Returns the RICHER raw shape (with cova/greenline/pos_mapping arrays)
  // so CategoryContext can hydrate the parent/child + POS mapping tree via
  // buildWebCategoryTree(). The thin `getList` above strips this metadata —
  // do NOT use it for the Pantry Category page.
  readWebCategoriesWithPOS: async (
    storeId: string,
  ): Promise<RawWebCategoryItem[]> => {
    const raw = (await httpClient.json.get(categoryUrl(storeId))) as
      | RawWebCategoryItem[]
      | { items?: RawWebCategoryItem[] }
      | null
    if (!raw) return []
    if (Array.isArray(raw)) return raw
    return Array.isArray(raw.items) ? raw.items : []
  },

  // Verified against legacy frontend/src/services/apis/category.ts:93-99 — POST /stores/:storeId/categories
  // with { item_data: {...} }. We send the same FULL payload shape as legacy context.tsx:263-280
  // toItemData(): name, slug, headline, description, visible, image, banner_image, seo, parent — even
  // when empty — because the backend validates the complete schema.
  // Response shape may be the bare category, { item }, or { data }; extractCreated() handles all three.
  create: async (
    storeId: string,
    input: CreateCategoryInput,
  ): Promise<CategoryItem | null> => {
    const itemData = buildItemData(input)

    // IMPORTANT: legacy categoryApi.createWebCategory uses httpClient.post (multipart/form-data),
    // NOT httpClient.json.post. The backend rejects JSON. jsonToFormData() (libs/bs-core/src/httpClient/utils.ts)
    // serializes the body so { item_data: {...} } becomes a single form field `item_data` whose value
    // is JSON.stringify(itemData).
    const raw = (await httpClient.post(categoryUrl(storeId), {
      item_data: itemData,
    })) as CreateCategoryResponse | ApiCategoryItem
    const created = extractCreated(raw)
    return created ? mapFromApi(created) : null
  },

  // Mirror of legacy frontend/src/services/apis/category.ts:101-108
  // updateWebCategory. PUT /stores/:storeId/categories/:categoryId with the
  // same multipart/form-data { item_data: {...} } body shape as create.
  // Response is not consumed by the UI (the UI invalidates the list query and
  // re-fetches) — returned as `unknown` to keep callers honest.
  update: async (
    storeId: string,
    categoryId: string,
    input: CreateCategoryInput,
  ): Promise<unknown> => {
    const itemData = buildItemData(input, categoryId)
    return httpClient.put(`${categoryUrl(storeId)}/${categoryId}`, {
      item_data: itemData,
    })
  },

  // Mirror of legacy frontend/src/services/apis/category.ts:110-111
  // deleteWebCategory. DELETE /stores/:storeId/categories/:categoryId.
  delete: async (storeId: string, categoryId: string): Promise<unknown> => {
    return httpClient.delete(`${categoryUrl(storeId)}/${categoryId}`)
  },

  // Bulk set `visible` true/false for many categories in one request.
  // PATCH /stores/:storeId/categories/bulk with { ids: number[], visible }.
  // Mirrors the Brand bulk-visibility contract (ProductBrandBulkView). Category
  // ids are numeric strings in the UI, coerced to numbers at the boundary.
  bulkUpdateVisible: async (
    storeId: string,
    ids: string[],
    visible: boolean,
  ): Promise<unknown> => {
    return httpClient.json.patch(`${categoryUrl(storeId)}/bulk`, {
      ids: ids.map(Number),
      visible,
    })
  },

  // Bulk delete many categories. Reuses the existing batch endpoint
  // PUT /stores/:storeId/categories with body { data: { delete: number[] } },
  // which routes to handle_delete_category_for_pantry (cascade-reparents
  // children, clears slug history, un-categorizes products, and rejects the
  // whole batch with a 400 if any id is POS-mapped). Ids are coerced to numbers.
  bulkDelete: async (storeId: string, ids: string[]): Promise<unknown> => {
    return httpClient.json.put(categoryUrl(storeId), {
      data: { delete: ids.map(Number) },
    })
  },

  // DELETE /stores/:storeId/categories/:categoryId/slug-history/:slugHistoryId
  // Removes a single active 301 redirect entry. Backend refuses to delete the
  // row that mirrors the category's current slug — that one is operational
  // scaffolding for the next slug rename — and returns 400 in that case.
  // `slugHistoryId` is the Mongo `_id` stringified (matching SlugHistoryEntry.id).
  deleteSlugHistory: async (
    storeId: string,
    categoryId: string,
    slugHistoryId: string,
  ): Promise<unknown> => {
    return httpClient.delete(
      `${categoryUrl(storeId)}/${categoryId}/slug-history/${slugHistoryId}`,
    )
  },

  // Unified, POS-agnostic many-to-many category mapping endpoint.
  // GET /pantry/stores/:storeId/category-mapping →
  //   { data: { "<posId>": [<webId>, <webId>] }, status, message }
  // httpClient.json.* unwraps res.data.data per the convention documented in
  // gateways/breadstack/customer/greenlineCustomerApis.ts:38, so the value we
  // receive is the inner mapping dict. Backend sends webIds as numbers; we
  // coerce everything to strings at the boundary so the UI can use them as
  // Map keys / Set members alongside the rest of MappingTab's `idStr()`
  // normalisation.
  readCategoryMapping: async (
    storeId: string,
  ): Promise<Record<string, string[]>> => {
    const raw = (await httpClient.json.get(
      `/pantry/stores/${storeId}/category-mapping`,
    )) as Record<string, Array<string | number>> | null
    if (!raw || typeof raw !== 'object') return {}
    const result: Record<string, string[]> = {}
    Object.entries(raw).forEach(([posId, webIds]) => {
      if (!Array.isArray(webIds)) return
      result[String(posId)] = webIds.map((id) => String(id))
    })
    return result
  },

  // PUT /pantry/stores/:storeId/category-mapping
  // Body wraps the dict under a `category_mapping` field per the backend
  // contract:
  //   { "category_mapping": { "<posId>": [<webId>, ...] } }
  // WebIds are sent back as numbers (the canonical backend type per the GET
  // response shape). Non-numeric webIds are passed through as-is, which lets
  // the backend reject malformed input without us swallowing it silently.
  updateCategoryMapping: async (
    storeId: string,
    mapping: Record<string, string[]>,
  ): Promise<unknown> => {
    const categoryMapping: Record<string, Array<number | string>> = {}
    Object.entries(mapping).forEach(([posId, webIds]) => {
      categoryMapping[posId] = webIds.map((id) => {
        const parsed = Number(id)
        return Number.isFinite(parsed) && String(parsed) === id ? parsed : id
      })
    })
    return httpClient.json.put(`/pantry/stores/${storeId}/category-mapping`, {
      category_mapping: categoryMapping,
    })
  },
}
